/**
 * Verified Listing: what a check is, what the badge covers, and when it stops
 * covering anything. Shared by the server route that records checks
 * (server/api/listing-verification.js) and the listing page that shows them,
 * so both apply one rule.
 *
 * ## Where the record lives
 *
 * - `metadata.vehicleVerification` — the public summary: which checks, their result,
 *   who ran them (a provider name or "Skip team"), when, and a fingerprint of
 *   the listing's material facts at that moment. Listing metadata can only be
 *   written with Integration API credentials, so a seller cannot set it.
 * - `privateData.vehicleVerificationEvidence` — the reviewer and the evidence
 *   references. Never shown publicly; nothing here reads it.
 *
 * ## What the badge means
 *
 * "Verified" appears only when every check on the approved checklist passed,
 * none is older than its maximum age, and the listing's material facts still
 * match the fingerprint. Each check is listed by name, so the badge never
 * claims more than was done: a VIN decode is not a history screen, a history
 * screen is not an ownership check, and none of them is an inspection.
 *
 * The field names are deliberately not `verification`: listing publications
 * (server/api-util/listingPublication) keep an unrelated post-publish snapshot
 * check under that name. Only top-level listing `metadata` is read here —
 * `publicData.metadata` (which the mobile listing route fills from the client)
 * is seller-writable and never counts.
 *
 * Each check records its `basis`, the kind of evidence it rests on, from a
 * fixed list per check. Ownership and inspection accept only documents of
 * that kind — a VIN decode is never a basis for either.
 *
 * Changing a material fact (VIN, year, make, model, mileage or its unit,
 * title) changes the
 * fingerprint, and every check recorded against the old facts stops counting
 * until it is recorded again. Nothing is backfilled.
 */

/** The checks a record may contain, with the plain-language name a buyer sees. */
export const CHECKS = {
  vin_decode: {
    label: 'VIN decoded and matched to the listing',
    maxAgeDays: null,
    bases: ['vin_decode'],
  },
  history_screening: {
    label: 'Stolen and salvage record screening',
    maxAgeDays: 90,
    // Recorded only by the server running the provider screen itself.
    bases: ['provider_screen'],
  },
  ownership: {
    label: 'Seller ownership confirmed',
    maxAgeDays: null,
    bases: ['vehicle_registration', 'ownership_permit', 'bill_of_sale'],
  },
  inspection: {
    label: 'Physical inspection',
    maxAgeDays: 365,
    bases: ['inspection_report', 'safety_certificate'],
  },
};

/** The checks the badge will require once the checklist is approved. */
export const REQUIRED_CHECKS = ['vin_decode', 'history_screening', 'ownership', 'inspection'];

/**
 * Whether the owner has approved REQUIRED_CHECKS as the Verified Listing
 * checklist. Until then no listing is marked Verified, however many checks
 * passed: individual checks are recorded and shown by name, the badge is not.
 */
export const CHECKLIST_APPROVED = false;

/** How far ahead of the server clock a checkedAt may be (clock skew), in ms. */
export const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;

export const RESULTS = ['pass', 'fail'];

const DAY_MS = 86400000;

/** Material facts: the ones a buyer relies on and a check was made against. */
const materialFacts = (title, publicData = {}) => ({
  title: typeof title === 'string' ? title.trim() : '',
  vin: typeof publicData.vin === 'string' ? publicData.vin.trim().toUpperCase() : '',
  year: publicData.year == null ? '' : String(publicData.year),
  make: typeof publicData.make === 'string' ? publicData.make.trim().toLowerCase() : '',
  model: typeof publicData.model === 'string' ? publicData.model.trim().toLowerCase() : '',
  // The web wizard writes `odometer` (km); the mobile and publication flows
  // write `mileage`, publications with `mileageUnit`. All three are material.
  odometer: publicData.odometer == null ? '' : String(publicData.odometer),
  mileage: publicData.mileage == null ? '' : String(publicData.mileage),
  mileageUnit:
    typeof publicData.mileageUnit === 'string' ? publicData.mileageUnit.trim().toLowerCase() : '',
});

/**
 * A short, stable fingerprint of the material facts. Not a security hash: it
 * only has to change when a fact changes, and it has to compute the same way
 * in the browser and on the server without a crypto dependency.
 */
export const materialFingerprint = (title, publicData) => {
  const facts = materialFacts(title, publicData);
  const text = Object.keys(facts)
    .sort()
    .map(k => `${k}=${facts[k]}`)
    .join('|');
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 ^ c, 2246822519) >>> 0;
  }
  return `v1-${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
};

const isIsoDate = v => typeof v === 'string' && !Number.isNaN(Date.parse(v));

/**
 * Validates one check, on write and again on read.
 *
 * @param {Object} c the check
 * @param {{ now?: Date }} [options] a check dated after `now` (beyond clock
 *   skew) is a problem: a mistyped 2099 would otherwise stay "current" for
 *   decades and bypass the freshness limit
 * @returns {string|null} a problem, or null when the check is well formed
 */
export const checkProblem = (c, { now = new Date() } = {}) => {
  if (!c || typeof c !== 'object') return 'check must be an object';
  if (!CHECKS[c.id]) return `unknown check ${c.id}`;
  if (!RESULTS.includes(c.result)) return `${c.id}: result must be pass or fail`;
  if (typeof c.provider !== 'string' || !c.provider.trim()) return `${c.id}: provider required`;
  if (!isIsoDate(c.checkedAt)) return `${c.id}: checkedAt must be an ISO date`;
  if (Date.parse(c.checkedAt) > now.getTime() + MAX_CLOCK_SKEW_MS) {
    return `${c.id}: checkedAt is in the future`;
  }
  if (!CHECKS[c.id].bases.includes(c.basis)) {
    return `${c.id}: basis must be one of ${CHECKS[c.id].bases.join(', ')}`;
  }
  return null;
};

/**
 * The state of a listing's verification, from the listing as the browser or
 * server holds it.
 *
 * @returns {{ verified: boolean, current: Array, stale: boolean, reason: string|null }}
 *   `current` lists the checks that still apply (passed or failed), `verified`
 *   is the badge, `stale` says the facts changed since the checks were made.
 */
export const verificationState = (
  listing,
  { now = new Date(), required = REQUIRED_CHECKS, checklistApproved = CHECKLIST_APPROVED } = {}
) => {
  const attrs = (listing && listing.attributes) || {};
  const record = attrs.metadata && attrs.metadata.vehicleVerification;
  if (record && record.status === 'revoked') {
    return { verified: false, current: [], stale: false, reason: 'revoked' };
  }
  if (!record || !Array.isArray(record.checks)) {
    return { verified: false, current: [], stale: false, reason: 'no checks recorded' };
  }
  if (record.fingerprint !== materialFingerprint(attrs.title, attrs.publicData)) {
    return { verified: false, current: [], stale: true, reason: 'listing changed since checks' };
  }
  const current = record.checks.filter(c => {
    if (checkProblem(c, { now })) return false;
    const max = CHECKS[c.id].maxAgeDays;
    return !max || now.getTime() - Date.parse(c.checkedAt) <= max * DAY_MS;
  });
  const passed = new Set(current.filter(c => c.result === 'pass').map(c => c.id));
  const missing = required.filter(id => !passed.has(id));
  if (missing.length) {
    return {
      verified: false,
      current,
      stale: false,
      reason: `missing or failed: ${missing.join(', ')}`,
    };
  }
  if (!checklistApproved) {
    return { verified: false, current, stale: false, reason: 'checklist pending approval' };
  }
  return { verified: true, current, stale: false, reason: null };
};

/** The sentence for a passed history screen, in the agreed wording. */
export const historyScreenSentence = (check, formatDate = d => d.slice(0, 10)) =>
  check && check.id === 'history_screening' && check.result === 'pass'
    ? `No stolen record found in ${check.provider} as of ${formatDate(check.checkedAt)}.`
    : null;

/**
 * The passed, current stolen and salvage record screen on a listing, or null. This is the only
 * vehicle-history information a listing shows ("Checks completed on this listing"), so it is the only
 * thing a buyer can be asked to acknowledge having reviewed.
 */
export const currentHistoryScreen = (listing, { now = new Date() } = {}) => {
  const state = verificationState(listing, { now });
  return state.current.find(c => c.id === 'history_screening' && c.result === 'pass') || null;
};
